/**
 * Exportação completa da fila de compras (PDF, Excel e CSV).
 *
 * A tabela mostra o que cabe na tela; a exportação leva tudo o que o sistema
 * sabe das requisições filtradas — principalmente as datas de cada etapa —
 * em abas: requisições, itens, cotações, pedidos dos solicitantes, ordens de
 * compra, recebimentos e histórico. Os dados vêm de
 * `POST /api/requisicoes/exportacao` (valores crus); aqui só se rotula.
 *
 * Coluna: { key, label, type?, valor(linha), chave? }. `chave` = identifica a
 * linha e se repete em cada parte do PDF quando a tabela é larga demais.
 */
import { SITUACAO_ITEM_LABEL, TIPO_TERCEIRO_LABEL } from "../../../services/compras";
import { SITUACAO_LABEL } from "../../../services/requisicoesCompraUi";
import { MODO_ITEM, textoDasCotacoes } from "./colunasFila";

const MAX_COLUNAS_PDF = 10;

const simNao = (v) => (v ? "Sim" : "Não");
const lista = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v);
const prioridade = (p) => (p === "URGENTE" ? "Urgente" : p ? "Normal" : "");
const TIPO_ITEM_LABEL = { CADASTRO: "Cadastrado", LIVRE: "Não cadastrado", TERCEIRO: "Terceiro" };

const c = (key, label, type, valor, extra = {}) => ({ key, label, type, valor: valor || ((l) => l[key]), ...extra });
const data = (key, label, valor) => c(key, label, "date", valor);
const dataHora = (key, label, valor) => c(key, label, "datetime", valor);
const dias = (key, label) => c(key, label, "number");

export const COLUNAS_EXPORT_REQUISICAO = [
  c("numero", "Nº", "text", null, { chave: true }),
  c("situacao", "Status", "text", (r) => SITUACAO_LABEL[r.situacao] || r.situacao_label),
  c("prioridade", "Prioridade", "text", (r) => prioridade(r.prioridade)),
  c("origem", "Origem", "text", (r) => r.origem_label),
  c("cliente_nome", "Cliente / obra", "text"),
  c("projeto_resumo", "Projeto", "text"),
  c("solicitantes", "Solicitante", "text", (r) => lista(r.solicitantes?.length ? r.solicitantes : [r.solicitado_por_nome])),
  c("comprador_nome", "Comprador", "text"),
  c("business_unit_nome", "Unidade", "text", (r) => r.business_unit_nome || r.business_unit_codigo),
  c("tipos_terceiro", "Tipos de terceiro", "text", (r) => lista((r.tipos_terceiro || []).map((t) => TIPO_TERCEIRO_LABEL[t] || t))),
  c("justificativa", "Justificativa", "text"),
  c("itens_total", "Itens", "number"),
  c("itens_pendentes", "Itens a cotar", "number"),
  c("itens_em_cotacao", "Itens em andamento", "number"),
  c("itens_aprovados", "Itens aprovados", "number"),
  c("itens_comprados", "Itens comprados", "number"),
  c("itens_cancelados", "Itens cancelados", "number"),
  dataHora("created_at", "Criada em"),
  data("data_necessaria", "Data necessária (recebimento)"),
  data("prazo_cotacao", "Prazo de cotação"),
  c("atrasada", "Cotação atrasada", "text", (r) => simNao(r.atrasada)),
  c("dias_para_prazo", "Dias para o prazo de cotação", "number"),
  dataHora("cotacao_iniciada_em", "Início da cotação"),
  dataHora("primeira_cotacao_em", "1ª cotação registrada"),
  dataHora("ultima_cotacao_em", "Última cotação registrada"),
  dataHora("primeira_resposta_em", "1ª resposta de fornecedor"),
  c("n_cotacoes", "Nº de cotações", "number"),
  c("n_fornecedores", "Nº de fornecedores cotados", "number"),
  dataHora("aprovado_em", "Aprovada em"),
  c("aprovado_por_nome", "Aprovada por", "text"),
  c("ordens_compra", "Ordens de compra", "text", (r) => lista(r.ordens_compra)),
  dataHora("primeira_oc_em", "1ª OC emitida em"),
  dataHora("ultima_oc_em", "Última OC emitida em"),
  c("valor_ocs", "Valor das OCs", "currency"),
  c("valor_em_aberto", "Valor em OC aberta", "currency", (r) => (Number(r.valor_em_aberto) ? r.valor_em_aberto : null)),
  data("previsao_entrega", "Previsão de entrega"),
  dataHora("primeiro_recebimento_em", "1º recebimento"),
  dataHora("ultimo_recebimento_em", "Último recebimento"),
  dataHora("comprado_ultimo_em", "Último item comprado em"),
  dataHora("atendida_em", "Atendida em"),
  dataHora("cancelada_em", "Cancelada em"),
  c("motivo_cancelamento", "Motivo do cancelamento", "text"),
  c("consolidadas", "Juntou", "text", (r) => lista((r.consolidadas || []).map((x) => x.numero))),
  c("consolidada_na_numero", "Juntada em (requisição)", "text"),
  dataHora("consolidada_em", "Juntada em (data)"),
  c("substitui_numero", "Substitui", "text"),
  c("requisicao_do_plano_numero", "Pedido de chapa do plano", "text"),
  dataHora("situacao_alterada_em", "Última mudança de status"),
  dataHora("updated_at", "Última alteração"),
  dias("dias_ate_cotar", "Dias até a 1ª cotação"),
  dias("dias_ate_aprovar", "Dias até aprovar"),
  dias("dias_ate_oc", "Dias até a 1ª OC"),
  dias("dias_ate_receber", "Dias até receber"),
  dias("dias_ate_fechar", "Dias até fechar"),
  c("cut_batch_id", "Lote PharuCut", "text"),
  c("lote_incompleto", "Lote incompleto", "text", (r) => (r.cut_batch_id ? simNao(r.lote_incompleto) : "")),
];

export const COLUNAS_EXPORT_ITEM = [
  c("requisicao_numero", "Nº", "text", null, { chave: true }),
  c("nome", "Item", "text", null, { chave: true }),
  c("tipo", "Tipo de item", "text", (i) => TIPO_ITEM_LABEL[i.tipo] || i.tipo),
  c("produto_codigo", "Código", "text"),
  c("categoria_label", "Categoria", "text"),
  c("marca", "Marca", "text"),
  c("modelo", "Modelo", "text"),
  c("especificacao", "Especificação", "text"),
  c("observacao", "Observação", "text"),
  c("tipo_terceiro_label", "Tipo de terceiro", "text"),
  c("qtd_solicitada", "Qtd. solicitada", "number"),
  c("qtd_aprovada", "Qtd. aprovada", "number"),
  c("unidade_efetiva", "Un.", "text"),
  c("cliente_efetivo", "Obra / cliente", "text"),
  c("projeto_resumo", "Projeto", "text"),
  c("solicitantes", "Solicitante", "text", (i) => lista(i.solicitantes)),
  c("origem_label", "Origem", "text"),
  c("requisicao_origem_numero", "Veio de (junção)", "text"),
  c("situacao", "Status do item", "text", (i) => SITUACAO_ITEM_LABEL[i.situacao] || i.situacao),
  c("requisicao_situacao", "Status da requisição", "text", (i) => SITUACAO_LABEL[i.requisicao_situacao] || i.requisicao_situacao),
  c("prioridade", "Prioridade", "text", (i) => prioridade(i.prioridade)),
  c("comprador_nome", "Comprador", "text"),
  dataHora("requisicao_criada_em", "Requisição criada em"),
  dataHora("item_incluido_em", "Item incluído em"),
  data("data_necessaria", "Data necessária (recebimento)"),
  data("prazo_cotacao", "Prazo de cotação"),
  dataHora("primeira_cotacao_em", "1ª cotação registrada"),
  dataHora("ultima_cotacao_em", "Última cotação registrada"),
  c("cotacoes", "Fornecedores cotados", "text", (i) => textoDasCotacoes(i.cotacoes_resumo)),
  c("fornecedores_nao_tem", "Fornecedores que não têm", "text", (i) => lista(i.fornecedores_nao_tem)),
  c("menor_total", "Menor preço", "currency"),
  c("menor_fornecedor", "Fornecedor do menor preço", "text"),
  c("vencedor_nome", "Vencedor", "text"),
  c("vencedor_total", "Valor do vencedor", "currency"),
  dataHora("vencedor_escolhido_em", "Vencedor escolhido em"),
  dataHora("aprovado_em", "Aprovado em"),
  dataHora("comprado_em", "Comprado em"),
  c("comprado_por_nome", "Comprado por", "text"),
  c("observacao_compra", "Observação da compra", "text"),
  c("ordem_compra_numero", "Ordem de compra", "text"),
  dataHora("oc_emitida_em", "OC emitida em"),
  c("oc_fornecedor", "Fornecedor da OC", "text"),
  c("oc_situacao", "Status da OC", "text"),
  data("oc_prazo_entrega", "Prazo de entrega da OC"),
  c("oc_qtd", "Qtd. na OC", "number"),
  c("oc_valor_unitario", "Valor unitário na OC", "currency"),
  c("qtd_recebida", "Qtd. recebida", "number"),
  dataHora("primeiro_recebimento_em", "1º recebimento"),
  dataHora("ultimo_recebimento_em", "Último recebimento"),
  c("entrega_obs", "Entrega / retirada", "text"),
  c("n_anexos", "Anexos", "number"),
  dias("dias_ate_cotar", "Dias até a 1ª cotação"),
  dias("dias_ate_comprar", "Dias até comprar"),
  dias("dias_ate_receber", "Dias até receber"),
];

export const COLUNAS_EXPORT_COTACAO = [
  c("requisicao_numero", "Nº", "text", null, { chave: true }),
  c("item", "Item", "text", null, { chave: true }),
  c("fornecedor", "Fornecedor", "text", null, { chave: true }),
  c("nao_tem", "Não tem", "text", (x) => simNao(x.nao_tem)),
  c("quantidade", "Quantidade", "number"),
  c("valor_unitario", "Valor unitário", "currency"),
  c("frete", "Frete", "currency"),
  c("total", "Total com frete", "currency"),
  c("prazo_dias", "Prazo (dias)", "number"),
  c("vencedora", "Vencedora", "text", (x) => simNao(x.vencedora)),
  dataHora("registrada_em", "Registrada em"),
  dataHora("enviada_em", "Enviada ao fornecedor em"),
  dataHora("respondida_em", "Respondida em"),
  dataHora("atualizada_em", "Última alteração"),
  c("registrada_por", "Registrada por", "text"),
  c("observacao", "Observação", "text"),
];

export const COLUNAS_EXPORT_PEDIDO = [
  c("requisicao_numero", "Nº", "text", null, { chave: true }),
  c("item", "Item", "text", null, { chave: true }),
  c("solicitante", "Solicitante", "text"),
  c("quantidade", "Quantidade", "number"),
  c("prioridade", "Prioridade", "text", (p) => prioridade(p.prioridade)),
  dataHora("pedido_em", "Pedido em"),
  data("data_necessaria", "Data necessária (recebimento)"),
  c("referencia", "Referência", "text"),
  c("observacao", "Observação", "text"),
];

export const COLUNAS_EXPORT_OC = [
  c("numero", "OC", "text", null, { chave: true }),
  c("requisicoes", "Requisições", "text", (o) => lista(o.requisicoes)),
  c("fornecedor", "Fornecedor", "text"),
  c("situacao", "Status", "text"),
  dataHora("emitida_em", "Emitida em"),
  c("emitida_por", "Emitida por", "text"),
  data("prazo_entrega", "Prazo de entrega"),
  c("n_itens", "Itens", "number"),
  c("valor_total", "Valor total", "currency"),
  c("observacao", "Observação", "text"),
];

export const COLUNAS_EXPORT_RECEBIMENTO = [
  c("ordem_compra", "OC", "text", null, { chave: true }),
  c("requisicoes", "Requisições", "text", (e) => lista(e.requisicoes)),
  c("produto", "Produto", "text"),
  c("qtd_recebida", "Qtd. recebida", "number"),
  c("custo_real", "Custo unitário real", "currency"),
  dataHora("recebido_em", "Recebido em"),
  c("recebido_por", "Recebido por", "text"),
  c("observacao", "Observação", "text"),
];

export const COLUNAS_EXPORT_EVENTO = [
  dataHora("timestamp", "Data e hora", null),
  c("requisicao_numero", "Nº", "text", null, { chave: true }),
  c("cliente_nome", "Cliente / obra", "text"),
  c("ator_nome", "Quem", "text"),
  c("detalhe", "O que aconteceu", "text"),
];

function secao(id, label, colunas, linhas = []) {
  return {
    id,
    label,
    columns: colunas.map(({ key, label: rotulo, type, chave }) => ({ key, label: rotulo, type, chave })),
    rows: linhas.map((l) => Object.fromEntries(colunas.map((col) => [col.key, col.valor(l)]))),
  };
}

const somaDinheiro = (linhas, campo) => linhas.reduce((t, l) => t + (Number(l[campo]) || 0), 0);

/** Resposta do backend -> { resumo, secoes } para salvarPDF/Excel/CSV. */
export function montarExportacaoCompleta(dados) {
  const d = dados || {};
  const reqs = d.requisicoes || [];
  const ocs = (d.ordens_compra || []).filter((o) => o.situacao !== "Cancelada");
  return {
    resumo: [
      { label: "Requisições", value: reqs.length, type: "number" },
      { label: "Itens", value: (d.itens || []).length, type: "number" },
      { label: "Cotações registradas", value: (d.cotacoes || []).length, type: "number" },
      { label: "Com cotação atrasada", value: reqs.filter((r) => r.atrasada).length, type: "number" },
      { label: "Ordens de compra (vivas)", value: ocs.length, type: "number" },
      { label: "Valor das ordens de compra", value: somaDinheiro(ocs, "valor_total"), type: "currency" },
      { label: "Recebimentos", value: (d.recebimentos || []).length, type: "number" },
    ],
    secoes: [
      secao("requisicoes", "Requisições", COLUNAS_EXPORT_REQUISICAO, reqs),
      secao("itens", "Itens", COLUNAS_EXPORT_ITEM, d.itens),
      secao("cotacoes", "Cotações", COLUNAS_EXPORT_COTACAO, d.cotacoes),
      secao("pedidos", "Pedidos dos solicitantes", COLUNAS_EXPORT_PEDIDO, d.pedidos),
      secao("ordens_compra", "Ordens de compra", COLUNAS_EXPORT_OC, d.ordens_compra),
      secao("recebimentos", "Recebimentos", COLUNAS_EXPORT_RECEBIMENTO, d.recebimentos),
      secao("historico", "Histórico", COLUNAS_EXPORT_EVENTO, d.eventos),
    ],
  };
}

/**
 * O PDF não rola para o lado: uma tabela com 50 colunas vira texto ilegível.
 * Cada seção larga é fatiada em partes de até `max` colunas, repetindo as
 * colunas-chave (Nº, item) para cada parte continuar legível sozinha.
 */
export function fatiarParaPdf(secoes, max = MAX_COLUNAS_PDF) {
  return secoes.flatMap((s) => {
    if (s.columns.length <= max) return [s];
    const chaves = s.columns.filter((col) => col.chave);
    const resto = s.columns.filter((col) => !col.chave);
    const porParte = Math.max(1, max - chaves.length);
    const total = Math.ceil(resto.length / porParte);
    return Array.from({ length: total }, (_, i) => ({
      ...s,
      id: `${s.id}_${i + 1}`,
      label: `${s.label} (${i + 1}/${total})`,
      columns: [...chaves, ...resto.slice(i * porParte, (i + 1) * porParte)],
    }));
  });
}

/** Ids das requisições das linhas na tela, na ordem da tela, sem repetir. */
export function idsDasLinhas(linhas, modo) {
  const ids = (linhas || []).map((l) => (modo === MODO_ITEM ? l.requisicao : l.id)).filter(Boolean);
  return [...new Set(ids)];
}
